import type { TCreateTripTaskReq } from 'api/swagger';
import type { TTripTaskState } from 'shared/stores/use-trip-task-store';
import { buildLocationPayload } from 'shared/utils/address';
import { CargoType } from 'types/cargo';
import { ETripTaskType } from 'types/trips';
import { validateCustomsForm } from './components/task-details/customs-task-form/utils';
import { validateLocationForm } from './components/task-details/location-task-form/utils';
import { validateReloadingFromForm } from './components/task-details/reloading-from-task-form/utils';
import { validateWarehouseForm } from './components/task-details/warehouse-task-form/utils';
import { ELocationMode } from './types';

export const validateDetailsStep = (state: TTripTaskState) => {
  const {
    taskType,
    locationMode,
    coordinates,
    address,
    warehouseId,
    loadingCustomAddress,
    unloadingCustomAddress,
    cargoTrips,
  } = state;

  if (!taskType) return false;

  if (taskType === ETripTaskType.return_asset) {
    const isSingleContainer = cargoTrips.length === 1 && cargoTrips[0].cargo.type === CargoType.CONTAINER;

    return isSingleContainer && validateLocationForm(locationMode, coordinates, address);
  }

  if (taskType === ETripTaskType.customs) {
    const hasLoadingCustomAddressInCargos = cargoTrips.some((cargoTrip) => cargoTrip.cargo.loadingCustomAddress);
    const hasUnloadingCustomAddressInCargos = cargoTrips.some((cargoTrip) => cargoTrip.cargo.unloadingCustomAddress);

    return validateCustomsForm(
      locationMode,
      coordinates,
      address,
      loadingCustomAddress,
      unloadingCustomAddress,
      hasLoadingCustomAddressInCargos,
      hasUnloadingCustomAddressInCargos,
    );
  }

  if (
    taskType === ETripTaskType.loading ||
    taskType === ETripTaskType.unloading ||
    taskType === ETripTaskType.reloading_to
  ) {
    return validateLocationForm(locationMode, coordinates, address);
  }

  if (taskType === ETripTaskType.loading_wh || taskType === ETripTaskType.unloading_wh) {
    return validateWarehouseForm(warehouseId);
  }

  if (taskType === ETripTaskType.reloading_from) {
    return validateReloadingFromForm(warehouseId, locationMode, coordinates, address);
  }

  return false;
};

const TASK_TYPES_WITHOUT_LOCATION = [ETripTaskType.loading_wh, ETripTaskType.unloading_wh, ETripTaskType.trailer_swap];

export const buildTripTaskPayload = (state: TTripTaskState, selectedCargoIds: number[]): TCreateTripTaskReq => {
  const basePayload: TCreateTripTaskReq = {
    type: state.taskType as ETripTaskType,
    cargoTripIds: selectedCargoIds,
    creationMode: state.taskType === ETripTaskType.return_asset ? 'separate_tasks' : state.creationMode,
    warehouseId: state.warehouseId,
  };

  if (state.taskType && TASK_TYPES_WITHOUT_LOCATION.includes(state.taskType)) {
    return basePayload;
  }

  if (state.locationMode === ELocationMode.fromCargo) {
    return { ...basePayload, autoAddress: 'cargo' };
  }

  if (state.locationMode === ELocationMode.loadingCustomAddress) {
    return { ...basePayload, autoAddress: 'loading_custom' };
  }

  if (state.locationMode === ELocationMode.unloadingCustomAddress) {
    return { ...basePayload, autoAddress: 'unloading_custom' };
  }

  return { ...basePayload, ...buildLocationPayload(state.locationMode, state.coordinates, state.address) };
};
